import { C, F } from '../../design-system/tokens';
import { useSwitchMembership } from './useSwitchMembership';
import type { Membership } from './membershipTypes';

// ── Role display maps (mirrors AdminPanel constants) ──────────────────────────

const ROLE_LABEL: Record<string, string> = {
  company_admin: 'Администратор',
  dispatcher: 'Диспетчер',
  technician: 'Техник',
};

const ROLE_COLOR: Record<string, string> = {
  company_admin: C.pu,
  dispatcher: C.bl,
  technician: C.gr,
};

// ── Sub-components ────────────────────────────────────────────────────────────

export function RolePill({ role }: { role: string }) {
  const color = ROLE_COLOR[role] ?? C.sub;
  return (
    <div
      style={{
        padding: '2px 7px',
        borderRadius: 5,
        background: `${color}18`,
        border: `1px solid ${color}33`,
        fontSize: 10,
        color,
        fontFamily: F.head,
        fontWeight: 700,
        letterSpacing: '.3px',
        flexShrink: 0,
      }}
    >
      {ROLE_LABEL[role] ?? role}
    </div>
  );
}

function MembershipRow({
  membership,
  isActive,
  switching,
  onSelect,
}: {
  membership: Membership;
  isActive: boolean;
  switching: boolean;
  onSelect: () => void;
}) {
  const roleColor = ROLE_COLOR[membership.role] ?? C.sub;

  return (
    <button
      onClick={onSelect}
      disabled={isActive || switching}
      aria-pressed={isActive}
      style={{
        width: '100%',
        padding: '9px 11px',
        borderRadius: 9,
        border: isActive ? `1.5px solid ${C.acc}55` : `1px solid ${C.b}`,
        background: isActive ? C.accS : C.surf,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 8,
        cursor: isActive || switching ? 'default' : 'pointer',
        opacity: switching && !isActive ? 0.45 : 1,
        transition: 'opacity 0.2s',
        textAlign: 'left',
      }}
    >
      {/* Company name + role */}
      <div>
        <div
          style={{
            fontFamily: F.head,
            fontWeight: 700,
            fontSize: 13,
            color: isActive ? C.acc : C.txt,
            lineHeight: 1.2,
          }}
        >
          {membership.company_name}
        </div>
        <div
          style={{
            fontSize: 10,
            color: roleColor,
            marginTop: 2,
            fontWeight: 600,
            letterSpacing: '.3px',
          }}
        >
          {ROLE_LABEL[membership.role] ?? membership.role}
        </div>
      </div>

      {/* State indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        {isActive ? (
          // Active dot
          <div
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: C.acc,
              boxShadow: `0 0 0 2px ${C.accS}`,
            }}
          />
        ) : (
          <div
            style={{
              fontSize: 10,
              color: C.sub,
              fontFamily: F.head,
              fontWeight: 700,
              letterSpacing: '.4px',
            }}
          >
            {switching ? '...' : 'ВОЙТИ'}
          </div>
        )}
      </div>
    </button>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * CompanySwitcher
 *
 * Drop-in component rendered inside each screen's account panel.
 *
 * Behaviour:
 * - 0 memberships  → renders nothing (data not yet loaded / single legacy user)
 * - 1 membership   → shows a static company chip with role, NO switch UI
 * - 2+ memberships → renders the full switcher: all companies as tappable
 *                    rows, active one highlighted, switching state shown
 *
 * This component is intentionally slim. It has no internal expanded/
 * collapsed state — the parent screen controls whether the account panel
 * is open at all.
 */
export function CompanySwitcher() {
  const { memberships, activeMembership, switching, switchMembership } =
    useSwitchMembership();

  // Nothing to render — store not yet populated
  if (memberships.length === 0) return null;

  // ── Single membership: nothing to switch, render nothing ────────────────
  if (memberships.length === 1) {
    return null;
  }

  // ── Multiple memberships: full switcher ───────────────────────────────────
  return (
    <div style={{ marginTop: 10 }}>
      {/* Section label */}
      <div
        style={{
          fontSize: 9,
          color: C.sub,
          textTransform: 'uppercase',
          letterSpacing: '.9px',
          fontFamily: F.head,
          fontWeight: 700,
          marginBottom: 7,
        }}
      >
        Компания
      </div>

      {/* Switching banner — briefly visible during the async switch */}
      {switching && (
        <div
          style={{
            padding: '6px 10px',
            borderRadius: 8,
            background: C.accS,
            border: `1px solid ${C.acc}33`,
            fontSize: 11,
            color: C.acc,
            fontFamily: F.head,
            fontWeight: 700,
            letterSpacing: '.3px',
            marginBottom: 7,
            textAlign: 'center',
          }}
        >
          Переключение...
        </div>
      )}

      {/* Membership rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {memberships.map((m) => (
          <MembershipRow
            key={m.membership_id}
            membership={m}
            isActive={m.membership_id === activeMembership?.membership_id}
            switching={switching}
            onSelect={() => void switchMembership(m)}
          />
        ))}
      </div>
    </div>
  );
}
